import React, { useEffect, useState } from "react";
import { ComposableMap, Geographies, Geography, ZoomableGroup } from "react-simple-maps";
import CircularProgress from "@mui/material/CircularProgress";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { DimensionamentoService } from "../../../services/DimensionamentoServices";

const interpolateColor = (color1, color2, factor) => {
  const c1 = color1.match(/\w\w/g).map((x) => parseInt(x, 16));
  const c2 = color2.match(/\w\w/g).map((x) => parseInt(x, 16));
  const result = c1.map((v, i) => Math.round(v + factor * (c2[i] - v)));
  return `rgb(${result[0]}, ${result[1]}, ${result[2]})`;
};

const DimensionamentoMap = ({ data, state, regionType, statusFilter, useGradient }) => {
  const [geoData, setGeoData] = useState(null);
  const [colors, setColors] = useState({});
  const [tooltips, setTooltips] = useState({});
  const [loading, setLoading] = useState(true);
  const [regionData, setRegionData] = useState({});

  useEffect(() => {
    const fetchRegionsAndData = async () => {
      if (regionType === "municipio") {
        setRegionData({});
        return;
      }

      const regionMap = {
        micro: "MUNICÍPIO SEDE MICRORREGIÃO",
        macro: "MUNICÍPIO SEDE MACRORREGIÃO",
      };

      try {
        const regionNames = await DimensionamentoService.getRegions(regionMap[regionType], state);
        const regionDataObj = {};

        for (const regionName of regionNames) {
          const response = await DimensionamentoService.getSearchType(regionName);
          const cities = response?.[`${regionType}Region`] || [];
          if (cities.length > 0) {
            regionDataObj[regionName] = cities;
          }
        }

        setRegionData(regionDataObj);
      } catch (e) {
        console.error("Erro ao carregar dados das regiões:", e);
      }
    };

    fetchRegionsAndData();
  }, [regionType, state]);

  useEffect(() => {
    const loadGeoData = async () => {
      setLoading(true);
      setColors({});
      setTooltips({});

      try {
        const response = await fetch(`jsons/geojs-${state}.json`);
        const geojson = await response.json();
        setGeoData(geojson);

        const cidadesGeo = geojson.features.map(f => f.properties.name);
        const regionCityMap = {};

        Object.entries(regionData).forEach(([region, cities]) => {
          cities.forEach(city => {
            regionCityMap[city] = region;
          });
        });

        const dadosFiltrados = data
          ?.filter(item => cidadesGeo.includes(item.city.city))
          .map(item => {
            const nomeCidade = item.city.city;
            const regionName = regionType === "municipio" ? nomeCidade : regionCityMap[nomeCidade];
            const available = Number(item.availableProvidersCity);
            const expected = Math.ceil(Number(item.expectedProvidersNeedsCity));
            const isValid = !isNaN(available) && !isNaN(expected);
            const result = isValid ? available - expected : 0;

            return { nomeCidade, result, available, expected, regionName };
          }) || [];

        const grouped = {};
        dadosFiltrados.forEach(({ regionName, result, available, expected }) => {
          if (!grouped[regionName]) grouped[regionName] = { result: 0, available: 0, expected: 0, count: 0 };
          grouped[regionName].result += result;
          grouped[regionName].available += available;
          grouped[regionName].expected += expected;
          grouped[regionName].count += 1;
        });

        const filteredGroups = Object.entries(grouped).filter(([_, reg]) => {
          return (
            (reg.result < 0 && statusFilter.insuficiente) ||
            (reg.result === 0 && statusFilter.suficiente) ||
            (reg.result > 0 && statusFilter.excedente)
          );
        });

        const resultsOnly = filteredGroups.map(([_, reg]) => reg.result);
        const minResult = Math.min(...resultsOnly);
        const maxResult = Math.max(...resultsOnly);

        const colorMap = {};
        const tooltipMap = {};

        const getColor = (result) => {
          if (!useGradient) {
            if (result < 0) return "#ff0000";
            if (result === 0) return "#007b49";
            return "#0066cc";
          }

          const { insuficiente, suficiente, excedente } = statusFilter;

          if (result < 0) {
            if (!insuficiente) return "#f0f0f0";
            const norm = (result - minResult) / ((0 - minResult) || 1);
            return interpolateColor("#ff0000", "#007b49", norm);
          } else if (result === 0) {
            if (!suficiente) return "#f0f0f0";
            return "#007b49";
          } else {
            if (!excedente) return "#f0f0f0";
            const norm = result / (maxResult || 1);
            return interpolateColor("#007b49", "#0066cc", norm);
          }
        };

        const isMunicipio = regionType === "municipio";
        const entityMap = isMunicipio ? Object.entries(grouped) : Object.entries(regionData);

        entityMap.forEach(([regionOrCity, cities]) => {
          const reg = isMunicipio ? grouped[regionOrCity] : grouped[regionOrCity];
          if (!reg) return;

          const show =
            (reg.result < 0 && statusFilter.insuficiente) ||
            (reg.result === 0 && statusFilter.suficiente) ||
            (reg.result > 0 && statusFilter.excedente);

          if (!show) return;

          const cor = getColor(reg.result);

          if (isMunicipio) {
            colorMap[regionOrCity] = cor;
            tooltipMap[regionOrCity] = `
              <b>${regionOrCity}</b><br/>
              Disponível: ${parseFloat(reg.available.toFixed(2))}<br/>
              Necessário: ${parseFloat(reg.expected.toFixed(2))}<br/>
              Suficiência: ${parseFloat(reg.result.toFixed(2))}
            `;
          } else {
            cities.forEach(city => {
              colorMap[city] = cor;
              tooltipMap[city] = `
                <b>${regionOrCity}</b><br/>
                Disponível: ${parseFloat(reg.available.toFixed(2))}<br/>
                Necessário: ${parseFloat(reg.expected.toFixed(2))}<br/>
                Suficiência: ${parseFloat(reg.result.toFixed(2))}
              `;
            });
          }
        });

        setColors(colorMap);
        setTooltips(tooltipMap);
      } catch (e) {
        console.error("Erro ao carregar GeoJSON ou processar dados:", e);
      }
      setLoading(false);
    };

    if (state && data?.length) {
      loadGeoData();
    }
  }, [state, data, statusFilter, regionData, regionType, useGradient]);

  if (loading || !geoData) {
    return (
      <div className="flex justify-center items-center w-full h-64">
        <CircularProgress />
      </div>
    );
  }

  const getScale = (jsonState) => {
    return 2000 + (1000 / (jsonState['north_south_distance_km'] ** 3)) * 400000000;
  };

  return (
    <div className="w-full h-full relative px-2 sm:px-0">
      <Tooltip id="tooltip-map" className="z-50" html />

      <ComposableMap
        projectionConfig={{
          scale: getScale(geoData),
          center: geoData.centroid?.coordinates || [-51.9, -24.7]
        }}
        style={{ width: "100%", height: "480px", transform: "scaleY(0.88)" }}
      >
        <ZoomableGroup center={geoData.centroid?.coordinates}>
          <Geographies geography={geoData}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const nomeCidade = geo.properties.name;
                const cor = colors[nomeCidade];
                const tooltip = tooltips[nomeCidade];

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    style={{
                      default: {
                        fill: cor || "#f6f6f6",
                        outline: "none",
                        stroke: "#686868",
                        strokeWidth: 0.7,
                        strokeLinejoin: "round",
                        strokeLinecap: "round",
                      },
                      hover: {
                        fill: "#9ca3af",
                        stroke: "#222",
                        strokeWidth: 1,
                        outline: "none",
                        cursor: "pointer",
                      },
                      pressed: {
                        fill: "#6b7280",
                        stroke: "#111",
                        strokeWidth: 1,
                        outline: "none",
                      }
                    }}
                    data-tooltip-id="tooltip-map"
                    data-tooltip-html={tooltip || nomeCidade}
                  />
                );
              })
            }
          </Geographies>
        </ZoomableGroup>
      </ComposableMap>

      {useGradient ? (
        <div className="mx-2 flex flex-col gap-2 items-center w-full justify-end text-xs sm:text-sm">
          <div
            className="w-full max-w-md h-4 rounded-full"
            style={{ background: 'linear-gradient(to right, #ff0000 0%, #007b49 50%, #0066cc 100%)' }}
          />
          <div className="flex justify-between w-full max-w-md text-xs text-gray-700 px-1">
            <span>Insuficiência de Rede</span>
            <span>Rede Suficiente</span>
            <span>Rede Excedente</span>
          </div>
        </div>
      ) : (
        <div className="flex gap-6 flex-wrap justify-center items-center text-sm">
          <div className="flex items-center gap-2">
            <div style={{ width: 20, height: 20, backgroundColor: "#ff0000", borderRadius: 3 }} />
            <span>Insuficiência de Rede</span>
          </div>
          <div className="flex items-center gap-2">
            <div style={{ width: 20, height: 20, backgroundColor: "#007b49", borderRadius: 3 }} />
            <span>Rede Suficiente</span>
          </div>
          <div className="flex items-center gap-2">
            <div style={{ width: 20, height: 20, backgroundColor: "#0066cc", borderRadius: 3 }} />
            <span>Rede Excedente</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default DimensionamentoMap;